import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import deprecateRenamedProperty from "web/core/dx/deprecateRenamedProperty";

/**
 * Component to organize form actions
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child elements (buttons, links, etc.)
 * @param {string} [props.type] - DEPRECATED: Use appearance instead
 * @param {('default'|'vertical'|'vertical-full'|'center'|'full'|'start'|'start-small'|'main-right')} [props.appearance] - Display style for actions
 * @param {boolean} [props.paddingBlock=true] - Add vertical padding
 * @param {boolean} [props.paddingInline=true] - Add horizontal padding
 */
const FormActions = ({
  children,
  type,
  appearance,
  paddingBlock = true,
  paddingInline = true,
}) => {
  if (!appearance) {
    appearance = deprecateRenamedProperty(
      "FormActions",
      "type",
      "appearance",
      type
    );
  }

  const childrenArray = (
    Array.isArray(children) ? children : [children]
  ).filter((e) => Boolean(e));

  return (
    <div
      className={classNames("form-actions", {
        [`form-actions--${appearance}`]: appearance,
        "form-actions--only-one": childrenArray.length <= 1,
        "form-actions--no-padding-block": !paddingBlock,
        "form-actions--no-padding-inline": !paddingInline,
      })}
    >
      {childrenArray.map((child, index) => {
        return (
          <div
            className="form-actions__element"
            key={child.key ?? `key-${index}`}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

FormActions.propTypes = {
  /**
   * Display style for form actions
   * - default: Standard alignment
   * - vertical: Stack actions vertically
   * - vertical-full: Stack actions vertically with full width
   * - center: Center actions horizontally
   * - full: Actions occupying full available width
   * - start: Align actions to start
   * - start-small: Align actions to start with reduced size
   * - main-right: Main action aligned to right
   * - end: Align actions to end
   */
  appearance: PropTypes.oneOf([
    "default",
    "vertical",
    "vertical-full",
    "center",
    "full",
    "start",
    "start-small",
    "main-right",
    "end",
  ]),
  children: PropTypes.node.isRequired,
  paddingBlock: PropTypes.bool,
  paddingInline: PropTypes.bool,
};

export default FormActions;
